import {
  CONTRACTS_MODULE_ID,
  currentUserProfileSchema,
} from "@raid-lens/contracts";
import { lazy, Suspense, useEffect, useState } from "react";

import { ApiError, apiGet, toSafeRequestFailure } from "./api";
import type { AuthenticationState } from "./authentication";
import { dashboardBuild } from "./build-info";
import { authenticatedGuildQueryCache } from "./guild-query-cache";
import { isModifiedNavigation, navigate, parseRoute } from "./routes";

interface DashboardAppProps {
  pathname: string;
}

const publicLinks = [
  ["Home", "/"],
  ["Guide", "/guide"],
  ["Privacy", "/privacy"],
  ["Terms", "/terms"],
  ["Support", "/support"],
] as const;

const loadAuthenticatedDashboard = () => import("./Dashboard");
const AuthenticatedDashboard = lazy(async () => ({
  default: (await loadAuthenticatedDashboard()).Dashboard,
}));

function preloadAuthenticatedDashboard() {
  void loadAuthenticatedDashboard();
}

export type { AuthenticationState } from "./authentication";

export function DashboardApp({ pathname }: DashboardAppProps) {
  const [currentPathname, setCurrentPathname] = useState(pathname);
  const [authentication, setAuthentication] = useState<AuthenticationState>({
    status: "loading",
  });
  const [authenticationReload, setAuthenticationReload] = useState(0);
  const route = parseRoute(currentPathname);
  const isDashboardRoute =
    route.page === "guilds" ||
    route.page.startsWith("guild-") ||
    route.page.startsWith("admin");

  useEffect(() => {
    authenticatedGuildQueryCache.beginBootstrap();
    const controller = new AbortController();
    apiGet("/api/v1/me", currentUserProfileSchema, controller.signal)
      .then((user) => {
        if (!controller.signal.aborted) {
          authenticatedGuildQueryCache.adoptPrincipal(user.id);
          setAuthentication({ status: "ready", user });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof ApiError && error.status === 401) {
          setAuthentication({ status: "signed-out" });
          return;
        }
        setAuthentication({
          status: "error",
          failure: toSafeRequestFailure(
            error,
            "The dashboard is temporarily offline.",
          ),
        });
      });
    return () => {
      controller.abort();
    };
  }, [authenticationReload]);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPathname(window.location.pathname);
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  function go(href: string) {
    navigate(href, setCurrentPathname);
  }

  return (
    <div className="site-frame" data-contracts-module={CONTRACTS_MODULE_ID}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="masthead">
        <a className="wordmark" href="/" aria-label="RaidLens home">
          <span className="wordmark-mark" aria-hidden="true">
            RL
          </span>
          <span>RaidLens</span>
        </a>
        <nav aria-label="Primary navigation">
          {publicLinks.map(([label, href]) => (
            <a
              key={href}
              href={href}
              aria-current={currentPathname === href ? "page" : undefined}
              onClick={(event) => {
                if (isModifiedNavigation(event)) return;
                event.preventDefault();
                go(href);
              }}
            >
              {label}
            </a>
          ))}
        </nav>
        {authentication.status === "ready" ? (
          <a
            className="masthead-login"
            href="/guilds"
            onClick={(event) => {
              if (isModifiedNavigation(event)) return;
              event.preventDefault();
              go("/guilds");
            }}
            onFocus={preloadAuthenticatedDashboard}
            onPointerEnter={preloadAuthenticatedDashboard}
          >
            Your guilds
          </a>
        ) : (
          <a className="masthead-login" href="/auth/discord">
            Sign in
          </a>
        )}
      </header>
      <main id="main-content" tabIndex={-1}>
        {route.page === "home" ? <HomePage /> : null}
        {route.page === "guide" ? <GuidePage /> : null}
        {route.page === "privacy" ? <PrivacyPage /> : null}
        {route.page === "terms" ? <TermsPage /> : null}
        {route.page === "support" ? <SupportPage /> : null}
        {isDashboardRoute ? (
          <Suspense fallback={<AuthenticatedDashboardFallback />}>
            <AuthenticatedDashboard
              route={route}
              pathname={currentPathname}
              go={go}
              authentication={authentication}
              setAuthentication={setAuthentication}
              reloadAuthentication={setAuthenticationReload}
            />
          </Suspense>
        ) : null}
        {route.page === "not-found" ? <NotFoundPage /> : null}
      </main>
      <footer className="footer">
        <div>
          <p>RaidLens, a natural-language guild assistant for Discord.</p>
          <p className="footer-build">{dashboardBuild}</p>
        </div>
        <p>
          Evidence answers identify available sources. Only exact, approved
          WoWAudit changes require confirmation in Discord.
        </p>
      </footer>
    </div>
  );
}

function AuthenticatedDashboardFallback() {
  return (
    <section className="dashboard-loading" aria-live="polite" aria-busy="true">
      <p className="eyebrow">Guild console</p>
      <h1>Loading dashboard</h1>
      <p>Opening your authenticated guild workspace.</p>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <section className="hero" aria-labelledby="home-title">
        <div className="hero-copy">
          <p className="eyebrow">Your guild assistant in Discord</p>
          <h1 id="home-title">
            Ask RaidLens about your guild. Keep the raid in sync.
          </h1>
          <p className="lede">
            RaidLens brings Warcraft Logs, player comparisons, rotations,
            current guides, guild knowledge, and carefully confirmed WoWAudit
            operations into one natural-language conversation.
          </p>
          <div className="action-row" aria-label="RaidLens actions">
            <a
              className="button button-primary"
              href="/auth/discord?intent=install"
            >
              Install for a guild
            </a>
            <a className="button button-secondary" href="/auth/discord">
              Sign in to dashboard
            </a>
            <a className="button button-secondary" href="/guide">
              See how RaidLens works
            </a>
          </div>
          <p className="availability">
            Dashboard access uses Discord sign-in. Guild policy decides who may
            ask, which channels are supported, and who may confirm an exact
            WoWAudit proposal.
          </p>
        </div>
        <QuestionBoard />
      </section>
      <section className="evidence-strip" aria-labelledby="evidence-title">
        <div>
          <p className="eyebrow">From question to raid call</p>
          <h2 id="evidence-title">
            Useful context without another command manual.
          </h2>
        </div>
        <dl>
          <div>
            <dt>01 / Ask</dt>
            <dd>
              Use the names, pulls, players, and plans your guild already knows.
            </dd>
          </div>
          <div>
            <dt>02 / Check</dt>
            <dd>
              RaidLens chooses the relevant tools and keeps source boundaries
              visible.
            </dd>
          </div>
          <div>
            <dt>03 / Coordinate</dt>
            <dd>
              Share the answer in Discord, or review an exact WoWAudit proposal
              before confirmation.
            </dd>
          </div>
        </dl>
      </section>
      <section className="capabilities" aria-labelledby="capabilities-title">
        <p className="eyebrow">Questions RaidLens can help with</p>
        <h2 id="capabilities-title">
          One conversation across the work of running a guild.
        </h2>
        <div className="capability-lines">
          <p>
            <strong>Warcraft Logs</strong>
            <span>
              &quot;Why did pull 12 fall apart after the second
              intermission?&quot;
            </span>
          </p>
          <p>
            <strong>Comparisons</strong>
            <span>
              &quot;Compare our Augmentation Evokers across the last five Mythic
              pulls.&quot;
            </span>
          </p>
          <p>
            <strong>Rotations</strong>
            <span>
              &quot;What should our Frost Death Knight prioritize during
              two-target cleave?&quot;
            </span>
          </p>
          <p>
            <strong>News and guides</strong>
            <span>
              &quot;What changed for Restoration Shaman this week, and which
              source says it?&quot;
            </span>
          </p>
          <p>
            <strong>Guild knowledge</strong>
            <span>
              &quot;What does our roster guide say about late absences?&quot;
            </span>
          </p>
          <p>
            <strong>WoWAudit</strong>
            <span>
              &quot;Move Thursday&apos;s raid to 20:00 and show the exact
              proposal before anyone confirms it in Discord.&quot;
            </span>
          </p>
        </div>
      </section>
    </>
  );
}

const coordinatedQuestions = [
  ["Logs", "Why did pull 12 unravel after intermission?", "Evidence ready"],
  ["Rotation", "What changes for Frost in two-target cleave?", "Guide checked"],
  ["Guild guide", "What is our late-absence rule?", "Guild source found"],
  [
    "WoWAudit",
    "Move Thursday's raid to 20:00.",
    "Discord confirmation required",
  ],
] as const;

function QuestionBoard() {
  return (
    <figure className="question-board" aria-labelledby="question-board-caption">
      <figcaption id="question-board-caption">
        <span>Tonight's ready check</span>
        <strong>4 questions in context</strong>
      </figcaption>
      <ol className="question-list">
        {coordinatedQuestions.map(([source, question, status], index) => (
          <li
            key={source}
            className={index === 3 ? "confirmation-question" : ""}
          >
            <span className="question-marker" aria-hidden="true">
              {String(index + 1).padStart(2, "0")}
            </span>
            <div>
              <p>{source}</p>
              <strong>{question}</strong>
              <small>{status}</small>
            </div>
          </li>
        ))}
      </ol>
      <p className="question-board-note">
        Evidence answers identify available sources. Exact, approved WoWAudit
        changes wait for an authorized confirmation in Discord.
      </p>
    </figure>
  );
}

const guideUseCases = [
  {
    label: "Pull diagnosis",
    title: "Turn a messy wipe into the next raid call.",
    prompt:
      "Why did pull 12 fall apart after the second intermission? Check deaths, missed interrupts, and avoidable damage.",
    note: "Best with a Warcraft Logs report and a specific pull.",
  },
  {
    label: "Player coaching",
    title: "Compare decisions, not just rankings.",
    prompt:
      "Compare our Augmentation Evokers across the last five Mythic pulls. Where do their cooldown timings diverge?",
    note: "Name the players, encounter, and the decision you are reviewing.",
  },
  {
    label: "Current guidance",
    title: "Connect observed play to current recommendations.",
    prompt:
      "What should our Frost Death Knight prioritize during two-target cleave, and how does that differ from this log?",
    note: "RaidLens keeps guide evidence separate from what happened in the log.",
  },
  {
    label: "Guild operations",
    title: "Find the rule or prepare the change.",
    prompt:
      "What does our roster guide say about late absences? Then move Thursday's raid to 20:00 and show the exact proposal.",
    note: "WoWAudit changes wait for an authorized confirmation in Discord.",
  },
] as const;

function GuidePage() {
  return (
    <article className="guide-page">
      <header className="guide-hero">
        <div>
          <p className="eyebrow">RaidLens field guide</p>
          <h1>Ask better raid questions. Get answers you can act on.</h1>
          <p className="lede">
            RaidLens works in the conversation your guild already has. Mention
            the bot in an allowed channel, give it the relevant raid context,
            and ask in plain language.
          </p>
          <div className="action-row">
            <a
              className="button button-primary"
              href="/auth/discord?intent=install"
            >
              Install for a guild
            </a>
            <a className="button button-secondary" href="#quick-start">
              Start with three steps
            </a>
          </div>
        </div>
        <aside className="guide-signal" aria-label="A good RaidLens question">
          <p>Question anatomy</p>
          <ol>
            <li>
              <span>01</span>
              <strong>Source</strong>
              <small>Report, guide, or guild document</small>
            </li>
            <li>
              <span>02</span>
              <strong>Scope</strong>
              <small>Encounter, pull, player, or date</small>
            </li>
            <li>
              <span>03</span>
              <strong>Decision</strong>
              <small>What should the team understand or change?</small>
            </li>
          </ol>
        </aside>
      </header>

      <section
        className="guide-quick-start"
        id="quick-start"
        aria-labelledby="quick-start-title"
      >
        <div className="guide-section-heading">
          <p className="eyebrow">Quick start</p>
          <h2 id="quick-start-title">From install to first answer.</h2>
        </div>
        <ol className="guide-steps">
          <li>
            <span>1</span>
            <div>
              <h3>Install and choose access</h3>
              <p>
                A server manager installs RaidLens, signs in to the dashboard,
                and chooses the channels and roles that may use it.
              </p>
            </div>
          </li>
          <li>
            <span>2</span>
            <div>
              <h3>Give the question context</h3>
              <p>
                Mention <strong>@RaidLens</strong> and include a report link,
                fight, player, or guild topic. You can also set a shared report
                with <code>/wcl report</code>.
              </p>
            </div>
          </li>
          <li>
            <span>3</span>
            <div>
              <h3>Follow the evidence</h3>
              <p>
                Ask follow-up questions in the same conversation. RaidLens
                identifies available sources and keeps exact WoWAudit changes
                behind Discord confirmation.
              </p>
            </div>
          </li>
        </ol>
      </section>

      <section className="guide-use-cases" aria-labelledby="use-cases-title">
        <div className="guide-section-heading">
          <p className="eyebrow">Copy-ready questions</p>
          <h2 id="use-cases-title">Four useful ways to bring RaidLens in.</h2>
        </div>
        <div className="guide-case-list">
          {guideUseCases.map((useCase, index) => (
            <article key={useCase.label}>
              <header>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{useCase.label}</p>
              </header>
              <h3>{useCase.title}</h3>
              <blockquote>{useCase.prompt}</blockquote>
              <small>{useCase.note}</small>
            </article>
          ))}
        </div>
      </section>

      <section className="guide-commands" aria-labelledby="commands-title">
        <div className="guide-section-heading">
          <p className="eyebrow">Commands</p>
          <h2 id="commands-title">A short command list, by design.</h2>
          <p>
            Most questions start with a normal @mention. Commands handle the few
            moments where explicit structure is useful.
          </p>
        </div>
        <dl>
          <div>
            <dt>
              <code>/help</code>
            </dt>
            <dd>Show the quick command summary and link back to this guide.</dd>
          </div>
          <div>
            <dt>
              <code>/wcl report</code>
            </dt>
            <dd>
              Set the active Warcraft Logs report and optional fight for the
              current channel or thread.
            </dd>
          </div>
          <div>
            <dt>
              <code>/wcl ask</code>
            </dt>
            <dd>
              Ask an evidence-based question with an optional report, fight,
              image, or text attachment.
            </dd>
          </div>
          <div>
            <dt>
              <code>/raidlens config</code>
            </dt>
            <dd>
              Let server managers review mention behavior and channel access.
            </dd>
          </div>
        </dl>
      </section>

      <section className="guide-cta" aria-labelledby="guide-cta-title">
        <p className="eyebrow">Ready check</p>
        <h2 id="guide-cta-title">
          Bring the question. RaidLens finds the lane.
        </h2>
        <p>
          Install the bot for your guild, then use <code>/help</code> in Discord
          whenever someone needs the short version.
        </p>
        <div className="action-row">
          <a
            className="button button-primary"
            href="/auth/discord?intent=install"
          >
            Install RaidLens
          </a>
          <a className="button button-secondary" href="/">
            Back to overview
          </a>
        </div>
      </section>
    </article>
  );
}

function DraftPage({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <article className="document-page">
      <header>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="draft-notice">
          Draft pending operator approval. This text is not final release
          policy.
        </p>
      </header>
      <div className="document-body">{children}</div>
    </article>
  );
}

function PrivacyPage() {
  return (
    <DraftPage eyebrow="Operator-review draft" title="Privacy outline">
      <section>
        <h2>Data categories</h2>
        <p>
          RaidLens may process Discord account and guild identifiers, permission
          snapshots, guild settings, operational audit records, and the report
          context supplied for a question, configured guild sources, sanitized
          WoWAudit connection status, and confirmed-operation audit records.
          Discord OAuth tokens are encrypted at rest, and dashboard sessions are
          active and time-limited.
        </p>
        <p>
          Guild selector synchronization stores only the guild owner user ID;
          role ID, name, position, and managed flag; non-bot member user ID and
          guild display name; and member-to-role relationships. RaidLens does
          not synchronize presences, email addresses, avatars, discriminators,
          messages, or other profile data for selectors.
        </p>
        <p>
          Stripe is the test-mode billing processor. RaidLens stores Stripe
          customer, subscription, and event identifiers plus minimized status,
          product, cancellation, timing, and reconciliation metadata. RaidLens
          does not store card or payment details.
        </p>
      </section>
      <section>
        <h2>Purpose</h2>
        <p>
          Data is used to answer questions about your guild, authorize guild
          administration, retain selected guild configuration, safely stage
          confirmed operations, protect the service, and diagnose reliability
          issues.
        </p>
        <p>
          Synchronized member and role data supports configuration selectors and
          guild ownership metadata. Synchronized member-to-role relationships
          are never used as runtime authorization.
        </p>
        <p>
          Stripe identifiers and minimized metadata are used to open test
          Checkout and the billing portal, reconcile current subscription
          access, recover missed events, prevent duplicate operations, and
          support billing reliability review.
        </p>
      </section>
      <section>
        <h2>Retention and processors</h2>
        <p>
          Members and roles missing from a complete snapshot are marked
          inactive. Unselected inactive selector rows are deleted after 30 days.
          Selected stale rows remain available so an administrator can repair
          the configuration, then become eligible for cleanup after selection
          removal. Selector metadata is deleted when the retained guild
          installation is purged. Authentication transactions and sessions are
          time-limited. Infrastructure processors include Discord, Cloudflare,
          Supabase, Stripe in test mode, and configured analysis providers.
          Completed Stripe event records and completed or abandoned Checkout
          attempts are intended to be deleted after 90 days in bounded
          maintenance batches. Final terms and retention remain subject to
          operator review and approval.
        </p>
      </section>
      <section>
        <h2>Deletion and contact</h2>
        <p>
          The operator must approve and publish an account and data request path
          before public release. Guild-owned settings may be retained or removed
          under the final approved guild lifecycle policy.
        </p>
      </section>
    </DraftPage>
  );
}

function TermsPage() {
  return (
    <DraftPage eyebrow="Operator-review draft" title="Terms outline">
      <section>
        <h2>Acceptable use</h2>
        <p>
          Use RaidLens only for guilds and reports you are authorized to access.
          Do not probe, disrupt, automate abuse against, or use the service to
          expose another person&apos;s private information.
        </p>
      </section>
      <section>
        <h2>Service availability</h2>
        <p>
          Answers and operations depend on Discord and configured providers.
          Features may be unavailable, delayed, changed, or withdrawn, and no
          uptime commitment is offered in this draft.
        </p>
      </section>
      <section>
        <h2>Limitations</h2>
        <p>
          RaidLens summarizes available evidence and can be incomplete or
          incorrect. Guild teams remain responsible for reviewing source data
          and deciding how to use an answer.
        </p>
      </section>
      <section>
        <h2>Abuse response</h2>
        <p>
          Access may be limited or removed to protect users, infrastructure,
          providers, or the service. A published abuse contact and final
          enforcement policy are required before release.
        </p>
      </section>
    </DraftPage>
  );
}

function SupportPage() {
  return (
    <DraftPage eyebrow="Operator-review draft" title="Support and incidents">
      <section>
        <h2>Request help</h2>
        <p>
          No public support address has been approved yet. The operator must add
          one before release. A request should include the affected Discord
          question or dashboard page, approximate time, and a non-sensitive
          description. Never send bot tokens, OAuth secrets, WoWAudit
          credentials, database credentials, or private report data.
        </p>
      </section>
      <section>
        <h2>Service incidents</h2>
        <p>
          The final support channel will carry confirmed incident notices and
          recovery updates. Discord command availability and dashboard
          availability may fail independently.
        </p>
      </section>
      <section>
        <h2>Security reports</h2>
        <p>
          A private security-reporting path and handling expectations will be
          published before release. Do not disclose exploitable details through
          a public guild channel.
        </p>
      </section>
    </DraftPage>
  );
}

function NotFoundPage() {
  return (
    <section className="not-found" aria-labelledby="not-found-title">
      <p className="eyebrow">Route not found / 404</p>
      <h1 id="not-found-title">This page is not in the raid plan.</h1>
      <p>Return to the public overview or open the draft support page.</p>
      <div className="action-row">
        <a className="button button-primary" href="/">
          Return home
        </a>
        <a className="button button-secondary" href="/support">
          Open support
        </a>
      </div>
    </section>
  );
}
